/* eslint-disable no-nested-ternary */
import React from "react";
import { cx } from "utils/join-classnames";
import { Modal, FormElement, TextField } from "ui/components";
import { useFormValidation } from "ui/hooks/use-form-validation";
import { createHlsStream, createRtmpStream } from "core/services";
import { useSessionContext } from "ui/context/SessionContext";
import { useNotifications } from "ui/context/NotificationsContext";
import styles from "./AddLivestreamModal.module.scss";

interface Props {
    onClose(): void;
}

const AddLivestreamModal: React.FC<Props> = ({ onClose }: Props) => {
    const sessionContext = useSessionContext();
    const { addNotification } = useNotifications();
    const [addLivestreamVisible, setAddLivestreamVisible] =
        React.useState<boolean>(true);
    const [rtmpDetailsVisible, setRtmpDetailsVisible] =
        React.useState<boolean>(false);
    const [hlsInputVisible, setHlsInputVisible] =
        React.useState<boolean>(false);
    const [isSendingRequest, setIsSendingRequest] =
        React.useState<boolean>(false);
    const [streamUrl, setStreamUrl] = React.useState<string>("");
    const [streamKey, setStreamKey] = React.useState<string>("");

    const addLivestreamForm = useFormValidation({
        streamTitle: {
            valid: false,
            touched: false,
            title: "Stream Title",
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 2,
            },
        },
        streamFormat: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                isEmpty: true,
            },
            defaultText: "Select your stream format",
            type: "dropdown",
            options: [
                {
                    id: -1,
                    title: "Select your stream format",
                    value: "",
                },
                {
                    id: 0,
                    title: "HLS Pull",
                    value: "hls",
                    description: "HLS URL",
                },
                {
                    id: 1,
                    title: "RTMP Push",
                    value: "rtmp",
                    description: "RTMP Endpoint",
                },
            ],
        },
    });

    const hlsForm = useFormValidation({
        pullUrl: {
            valid: true,
            touched: false,
            title: "HLS Pull URL",
            value: "",
            errorMsg: "",
            rules: {
                isMatchingPattern:
                    "((https|http)://)((\\w|-)+)(([.]|[/])((\\w|-)+))+",
            },
        },
    });

    const createRtmpEndpoint = async () => {
        try {
            setIsSendingRequest(true);
            if (!sessionContext.session || !sessionContext.selectedProject) {
                throw Error("no session or project");
            }
            if (!addLivestreamForm.formState.streamTitle.value) {
                throw Error("Stream title was missing");
            }
            const result = await createRtmpStream(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                addLivestreamForm.formState.streamTitle.value.toString(),
            );
            if (result.error) {
                throw Error(result.error);
            }
            setIsSendingRequest(false);
            setAddLivestreamVisible(false);
            setRtmpDetailsVisible(true);

            if (result.value) {
                setStreamKey(result.value.mediaEngineStreamKey);
                setStreamUrl(result.value.mediaEngineStream.rtmpUrl);
            }
            addNotification(
                "success",
                "RTMP Livestream created",
                "You're now able to go live!",
                5000,
            );
        } catch (err) {
            setIsSendingRequest(false);
            onClose();
            addNotification(
                "error",
                "Error while creating",
                "Please try again",
                0,
            );
        }
    };

    const handleStreamType = () => {
        if (addLivestreamForm.formState.streamFormat.value === "hls") {
            setHlsInputVisible(true);
            setAddLivestreamVisible(false);
        } else if (addLivestreamForm.formState.streamFormat.value === "rtmp") {
            createRtmpEndpoint();
        }
    };

    const handleAddHlsStream = async () => {
        try {
            setIsSendingRequest(true);
            if (!sessionContext.session || !sessionContext.selectedProject) {
                throw Error("no session or project");
            }
            if (!addLivestreamForm.formState.streamTitle.value) {
                throw Error("Stream title was missing");
            }
            if (!hlsForm.formState.pullUrl.value) {
                throw Error("HLS Pull URL was missing");
            }

            const result = await createHlsStream(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                addLivestreamForm.formState.streamTitle.value.toString(),
                hlsForm.formState.pullUrl.value.toString(),
            );
            if (result.error) {
                throw Error(result.error);
            }
            setIsSendingRequest(false);
            addNotification(
                "success",
                "HLS Livestream created",
                "You're now able to go live!",
                5000,
            );
            onClose();
        } catch (err) {
            setIsSendingRequest(false);
            onClose();
            addNotification(
                "error",
                "Error while creating",
                "Please try again",
                0,
            );
        }
    };

    const getSaveButtonText = () => {
        if (addLivestreamVisible && !isSendingRequest) {
            return "Continue";
        }

        if (rtmpDetailsVisible && !isSendingRequest) {
            return "Got it!";
        }

        if (hlsInputVisible && !isSendingRequest) {
            return "Add Stream";
        }

        if (isSendingRequest) {
            return "Creating…";
        }

        return "";
    };

    const getSaveButtonDisabledState = (): boolean => {
        if (addLivestreamVisible && !isSendingRequest) {
            return !addLivestreamForm.formValid;
        }

        if (rtmpDetailsVisible && !isSendingRequest) {
            return false;
        }

        if (hlsInputVisible && !isSendingRequest) {
            return !hlsForm.formValid;
        }

        if (isSendingRequest) {
            return true;
        }

        return false;
    };

    const modalContent = (
        <>
            {addLivestreamVisible && (
                <div className={styles.container}>
                    <FormElement
                        type="text"
                        theme="dark"
                        title={
                            addLivestreamForm.formState.streamTitle.title || ""
                        }
                        id="newStreamTitle"
                        onChange={(value) => {
                            addLivestreamForm.inputChangeHandler(
                                "streamTitle",
                                value,
                            );
                        }}
                        defaultText="Name of your stream"
                        fieldState={addLivestreamForm.formState.streamTitle}
                    />
                    <FormElement
                        id="newStreamFormat"
                        theme="dark"
                        type="dropdown"
                        onChange={(value) => {
                            addLivestreamForm.inputChangeHandler(
                                "streamFormat",
                                value,
                            );
                        }}
                        title="Stream Format"
                        fieldState={addLivestreamForm.formState.streamFormat}
                        options={
                            addLivestreamForm.formState.streamFormat.options
                        }
                    />
                </div>
            )}
            {rtmpDetailsVisible && (
                <div className={styles.container}>
                    <div className={cx(styles.formelement, styles.infotext)}>
                        Broadcast your RTMP stream towards
                    </div>
                    <div className={styles.streamDetailsMargins}>
                        <label className={styles.formelement__label}>
                            RTMP Stream URL
                        </label>
                        <TextField
                            theme="dark"
                            defaultText="RTMP Stream URL"
                            value={streamUrl}
                            copiable
                            disabled
                        />
                    </div>
                    <div className={styles.streamDetailsMargins}>
                        <label className={styles.formelement__label}>
                            RTMP Stream Key
                        </label>

                        <TextField
                            theme="dark"
                            defaultText="RTMP Stream Key"
                            value={streamKey}
                            copiable
                            disabled
                        />
                    </div>
                </div>
            )}

            {hlsInputVisible && (
                <div className={styles.container}>
                    <FormElement
                        type="text"
                        theme="dark"
                        title="HLS URL"
                        id="pullUrl"
                        onChange={(value) => {
                            hlsForm.inputChangeHandler("pullUrl", value);
                        }}
                        fieldState={hlsForm.formState.pullUrl}
                        defaultText="https://domain.com/playlist.m3u8"
                    />
                </div>
            )}
        </>
    );

    return (
        <Modal
            content={modalContent}
            lineColor="amber"
            onClose={onClose}
            theme="dark"
            onSave={
                addLivestreamVisible
                    ? handleStreamType
                    : rtmpDetailsVisible
                      ? onClose
                      : hlsInputVisible
                        ? handleAddHlsStream
                        : () => {}
            }
            saveButtonText={getSaveButtonText()}
            saveButtonDisabled={getSaveButtonDisabledState()}
            title="Connect your stream"
            icon="plus"
            takeContentSize
        />
    );
};

export default AddLivestreamModal;
